import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Lock, TrendingUp } from "lucide-react";
import SEO from "../components/seo/SEO";
import { CATEGORIES } from "../config/categories";
import { CALCULATORS, getCalculatorsByCategory } from "../config/calculators";
import CalculatorCard from "../components/calculator/CalculatorCard";
import { Icon } from "../components/common/Icon";
import EmailSignup from "../components/common/EmailSignup";
import { PRODUCTS } from "../config/products";
import { ProductCard } from "../components/product/ProductCard";
import AdPlaceholder from "../components/common/AdPlaceholder";

const HERO_BG = "https://images.unsplash.com/photo-1628620859117-4f46908a1c49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxjYW5hZGlhbiUyMGxhbmRzY2FwZSUyMG1pbmltYWwlMjBzdW5zZXR8ZW58MHx8fHwxNzg2Mzg4NDczfDA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const featured = CALCULATORS.slice(0, 6);
  return (
    <>
      <SEO
        title="MyFinancesGuru Tools — Free Canadian Financial Calculators (2025)"
        description="Free, trustworthy Canadian financial calculators: mortgage, TFSA, RRSP, FHSA, retirement, debt payoff and more. Simple, accurate, Canada-specific. Calculations run privately in your browser."
        path="/"
        keywords={["canadian financial calculators", "mortgage calculator canada", "tfsa calculator", "rrsp calculator", "retirement calculator canada"]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-texture opacity-60" />
        <img src={HERO_BG} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-[0.06]" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Make smarter money decisions with <span className="text-brand">free Canadian</span> financial tools.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              Mortgage, TFSA, RRSP, FHSA, retirement, debt payoff and more — accurate, Canada-specific calculators with plain-language explanations. Free value first, always.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/calculators" data-testid="hero-cta-calculators" className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover">
                Explore all calculators <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/products" data-testid="hero-cta-products" className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-7 py-3.5 text-sm font-semibold text-foreground transition-colors hover:border-brand hover:text-brand">
                Browse planners
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2"><Lock className="h-4 w-4 text-brand" /> Private — runs in your browser</span>
              <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-brand" /> No sign-up required</span>
              <span className="inline-flex items-center gap-2"><TrendingUp className="h-4 w-4 text-brand" /> Dated & sourced Canadian data</span>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">Browse by category</h2>
          <p className="mt-3 text-muted-foreground">Every tool is free, works on mobile, and explains the Canadian rules behind the numbers.</p>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CATEGORIES.map((cat) => {
            const count = getCalculatorsByCategory(cat.id).length;
            return (
              <Link key={cat.id} to={`/calculators/category/${cat.id}`} data-testid={`category-card-${cat.id}`}
                className="group flex items-start gap-4 rounded-2xl border border-border bg-card p-6 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand/10 text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                  <Icon name={cat.icon} className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground">{cat.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{cat.description}</p>
                  <span className="mt-2 inline-block text-xs font-semibold uppercase tracking-wide text-action">{count} tools</span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Popular calculators */}
      <section className="bg-[#F3F6F4] py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">Popular calculators</h2>
            <Link to="/calculators" className="hidden items-center gap-1 text-sm font-semibold text-brand hover:text-action sm:inline-flex">View all <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((c) => <CalculatorCard key={c.slug} calc={c} />)}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8"><AdPlaceholder slot="home-mid" /></div>

      {/* Products teaser */}
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-4xl">Go further with our planners</h2>
          <p className="mt-3 text-muted-foreground">Ready-made Google Sheets templates to organize your whole financial life. Instant download via Gumroad.</p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PRODUCTS.slice(0, 3).map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
        <div className="mt-8"><Link to="/products" className="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:text-action">See all planners <ArrowRight className="h-4 w-4" /></Link></div>
      </section>

      {/* Lead magnet */}
      <section className="mx-auto max-w-3xl px-4 py-20 sm:px-6 lg:px-8">
        <EmailSignup source="homepage" />
      </section>
    </>
  );
}
